/**
 * Holland gradient wind profile.
 *
 * Source: Holland, G. J. (1980). An Analytic Model of the Wind and Pressure
 * Profiles in Hurricanes. Monthly Weather Review, 108, 1212-1218.
 *
 *   p(r) = pc + dp * exp(-(Rmax/r)^B)
 *   V(r) = sqrt( (B/rho) * dp * (Rmax/r)^B * exp(-(Rmax/r)^B) + (r f / 2)^2 ) - r f / 2
 *
 * with dp = p_ambient - p_central and f = 2 * Omega * sin(latitude).
 *
 * The cyclostrophic part peaks at r = Rmax exactly: with x = (Rmax/r)^B the
 * term x e^(-x) is maximised at x = 1. Coriolis shifts the peak slightly.
 */
import {
  EARTH_OMEGA_RAD_S,
  HOLLAND_B_MAX,
  HOLLAND_B_MIN,
  HOLLAND_MIN_RADIUS_M,
} from '../config/constants';

export interface HollandParams {
  pCentralPa: number;
  pAmbientPa: number;
  rMaxM: number;
  B: number;
  /** Positive = Northern Hemisphere. */
  latitudeDeg: number;
  /** kg/m^3 */
  airDensity: number;
}

/** Holland (1980) reports plausible B in roughly [1, 2.5]. */
export function clampB(B: number): number {
  return B < HOLLAND_B_MIN ? HOLLAND_B_MIN : B > HOLLAND_B_MAX ? HOLLAND_B_MAX : B;
}

/** Coriolis parameter f = 2 Omega sin(phi), rad/s. Signed by hemisphere. */
export function coriolisParameter(latitudeDeg: number): number {
  return 2 * EARTH_OMEGA_RAD_S * Math.sin((latitudeDeg * Math.PI) / 180);
}

/**
 * +1 for counterclockwise viewed from above (Northern Hemisphere), -1 for
 * clockwise (Southern Hemisphere). The equator returns +1 by convention.
 */
export function rotationSign(latitudeDeg: number): 1 | -1 {
  return latitudeDeg < 0 ? -1 : 1;
}

/**
 * Hot-path variant: caller supplies |f| so sin() is not evaluated per sample.
 * Returns speed magnitude in m/s (always >= 0).
 */
export function hollandSpeedWithF(rM: number, p: HollandParams, fAbs: number): number {
  if (rM < HOLLAND_MIN_RADIUS_M) return 0;
  const B = clampB(p.B);
  const dp = p.pAmbientPa - p.pCentralPa;
  const x = Math.pow(p.rMaxM / rM, B);
  const cyclostrophic = (B / p.airDensity) * dp * x * Math.exp(-x);
  const half = (rM * fAbs) / 2;
  const v = Math.sqrt(cyclostrophic + half * half) - half;
  return v > 0 ? v : 0;
}

/** Gradient wind speed magnitude at radius rM, m/s. Zero for r < 1 m. */
export function hollandTangentialSpeed(rM: number, p: HollandParams): number {
  return hollandSpeedWithF(rM, p, Math.abs(coriolisParameter(p.latitudeDeg)));
}

/** Surface pressure at radius rM, Pa. Tends to pc as r -> 0 and p_ambient as r -> inf. */
export function hollandPressure(rM: number, p: HollandParams): number {
  if (rM <= 0) return p.pCentralPa;
  const B = clampB(p.B);
  const dp = p.pAmbientPa - p.pCentralPa;
  return p.pCentralPa + dp * Math.exp(-Math.pow(p.rMaxM / rM, B));
}
